import React from "react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";

interface NavigationButtonsProps {
  previousInfo: {
    pageName: string;
    pagePath: string;
    isActive?: boolean;
  };
  /** Omitted on screens without a Next step (the experience selector and the age-group
      step, which navigate on a single tap), where Next is not rendered at all. */
  nextInfo?: {
    pageName: string;
    pagePath: string;
    isActive?: boolean;
    component?: React.ReactElement;
  };
  isMobile: boolean;
  isAddingToCart: boolean;
  generateNextButtonBgColor: string;
  onPreviousClick: () => void;
  onNextClick: () => void;
  /** Hide Back entirely (e.g. on /, where there is nowhere to go back to). */
  showBackButton?: boolean;
  /** Render a Checkout button in place of Next (e.g. on / with cart items + add-another-attraction). */
  showCheckoutButton?: boolean;
  onCheckoutClick?: () => void;
  /** When false, hides the down chevron on the Checkout button only. Next keeps its chevron. */
  showCheckoutChevron?: boolean;
}

/** Layout only. Theme background/radius/colour come from a separate `booking-*-pill` class. */
const PILL_BASE_CLASS = "tw-font-[700] tw-flex tw-items-center tw-justify-center tw-gap-[2px] tw-h-[40px] lg:tw-h-[54px]";

const CTA_PILL_BASE_CLASS = `booking-cta-pill ${PILL_BASE_CLASS}`;

/** Next with chevron: less trailing padding on the chevron side. */
const NEXT_PILL_CLASS = `${CTA_PILL_BASE_CLASS} tw-pl-[20px] tw-pr-[14px] lg:tw-pl-[28px] lg:tw-pr-[20px] tw-py-[8px] lg:tw-py-[18px]`;
/** Symmetric padding; min-width matches Next+chevron so Checkout stays centered without a ghost chevron slot. */
const ROOT_CROSSFADE_PILL_CLASS = `${CTA_PILL_BASE_CLASS} tw-min-w-[134px] tw-px-[17px] lg:tw-min-w-[152px] lg:tw-px-[24px] tw-py-[8px] lg:tw-py-[18px]`;

/** Checkout with chevron: same asymmetric balance as Next. */
const CHECKOUT_PILL_WITH_CHEVRON_CLASS = `${CTA_PILL_BASE_CLASS} tw-pl-[22px] tw-pr-[14px] lg:tw-pl-[30px] lg:tw-pr-[20px] tw-py-[8px] lg:tw-py-[18px]`;

/** Tighter leading padding on the chevron side to offset MD icon viewBox inset. */
const PREVIOUS_PILL_CLASS = `booking-prev-pill ${PILL_BASE_CLASS} tw-pl-[14px] tw-pr-[20px] lg:tw-pl-[20px] lg:tw-pr-[28px] tw-py-[8px] lg:tw-py-[18px]`;

const CHEVRON_SIZE_CLASS = "tw-block tw-h-[40px] tw-w-[40px] tw-max-w-none tw-leading-none lg:tw-h-[48px] lg:tw-w-[48px]";

const ACTION_LABEL_CROSSFADE_CLASS =
  "tw-transition-opacity tw-duration-[600ms] tw-ease-[cubic-bezier(0.16,1,0.3,1)]";

const ACTION_CHEVRON_REVEAL_CLASS =
  "tw-inline-flex tw-shrink-0 tw-overflow-hidden tw-transition-[opacity,max-width] tw-duration-[600ms] tw-ease-[cubic-bezier(0.16,1,0.3,1)]";

const NavChevronUp = () => (
  <span className="tw-inline-flex tw-w-[26px] tw-shrink-0 tw-items-center tw-justify-start tw-overflow-visible lg:tw-w-[30px]" aria-hidden>
    <MdKeyboardArrowUp className={`${CHEVRON_SIZE_CLASS} -tw-translate-x-[4px]`} />
  </span>
);

const NavChevronDown = () => (
  <span className="tw-inline-flex tw-w-[26px] tw-shrink-0 tw-items-center tw-justify-end tw-overflow-visible lg:tw-w-[30px]" aria-hidden>
    <MdKeyboardArrowDown className={CHEVRON_SIZE_CLASS} />
  </span>
);

const renderChevronDownSlot = (visible: boolean) => (
  <span
    className={`${ACTION_CHEVRON_REVEAL_CLASS} ${visible ? "tw-max-w-[26px] tw-opacity-100 lg:tw-max-w-[30px]" : "tw-max-w-0 tw-opacity-0"}`}
    aria-hidden={!visible}
  >
    <NavChevronDown />
  </span>
);

/** The label fading out of a Checkout ↔ Next crossfade: out of flow, centred over the visible one. */
const CROSSFADE_HIDDEN_LABEL_CLASS = "tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-opacity-0";

/** Checkout ↔ Next crossfade. Only the visible label takes up space, so the chevron sits right
    beside it; the fading-out label is overlaid on it. */
const renderCrossfadedActionLabel = (checkoutLabel: string, nextLabel: string, showCheckout: boolean) => (
  <span className="tw-relative tw-inline-flex tw-shrink-0 tw-items-center tw-whitespace-nowrap">
    <span
      className={`tw-text-[14px] tw-font-[700] tw-leading-none tw-text-white ${ACTION_LABEL_CROSSFADE_CLASS} ${
        showCheckout ? "tw-opacity-100" : CROSSFADE_HIDDEN_LABEL_CLASS
      }`}
      aria-hidden={!showCheckout}
    >
      {checkoutLabel}
    </span>
    <span
      className={`tw-text-[14px] tw-font-[700] tw-leading-none tw-text-white ${ACTION_LABEL_CROSSFADE_CLASS} ${
        showCheckout ? CROSSFADE_HIDDEN_LABEL_CLASS : "tw-opacity-100"
      }`}
      aria-hidden={showCheckout}
    >
      {nextLabel}
    </span>
  </span>
);

export const NavigationButtons: React.FC<NavigationButtonsProps> = React.memo(
  ({
    previousInfo,
    nextInfo,
    isMobile,
    isAddingToCart,
    generateNextButtonBgColor,
    onPreviousClick,
    onNextClick,
    showBackButton = true,
    showCheckoutButton = false,
    onCheckoutClick,
    showCheckoutChevron = true,
  }) => {
    const isRootNavPill = !showCheckoutChevron;
    const showRootCrossfadeAction = isRootNavPill && (showCheckoutButton || !!nextInfo);
    const showCheckoutLabel = showCheckoutButton;
    const checkoutLabel = isAddingToCart ? "Adding to cart..." : "Checkout";
    const nextLabel = isAddingToCart ? "Adding to cart..." : (nextInfo?.pageName ?? "Next");
    const isRootActionDisabled = isAddingToCart || (!showCheckoutLabel && !nextInfo?.isActive);

    return (
      <div className="tw-flex tw-flex-row tw-gap-2 tw-py-2">
        {showBackButton && (
          <button className={PREVIOUS_PILL_CLASS} onClick={onPreviousClick}>
            <NavChevronUp />
            <p className="tw-text-[14px] tw-font-[700]">{isMobile ? "Back" : previousInfo.pageName}</p>
          </button>
        )}

        {showRootCrossfadeAction ? (
          <button
            className={`${ROOT_CROSSFADE_PILL_CLASS} tw-transition-opacity tw-duration-200 ${
              isRootActionDisabled ? "booking-cta-pill-disabled tw-cursor-not-allowed" : `${generateNextButtonBgColor} hover:tw-opacity-90`
            }`}
            onClick={isRootActionDisabled ? undefined : showCheckoutLabel ? onCheckoutClick : onNextClick}
            disabled={isRootActionDisabled}
          >
            {renderCrossfadedActionLabel(checkoutLabel, nextLabel, showCheckoutLabel)}
            {renderChevronDownSlot(!showCheckoutLabel)}
          </button>
        ) : showCheckoutButton ? (
          <button
            className={`${CHECKOUT_PILL_WITH_CHEVRON_CLASS} ${isAddingToCart ? "booking-cta-pill-disabled tw-cursor-not-allowed" : generateNextButtonBgColor}`}
            onClick={onCheckoutClick}
            disabled={isAddingToCart}
          >
            <p className="tw-whitespace-nowrap tw-text-[14px] tw-font-[700] tw-text-white">{checkoutLabel}</p>
            <NavChevronDown />
          </button>
        ) : nextInfo ? (
          nextInfo.isActive && nextInfo.component ? (
            <div className="tw-flex tw-items-center tw-gap-2">
              <button className={`${NEXT_PILL_CLASS} ${generateNextButtonBgColor}`} onClick={onNextClick}>
                <p className="tw-whitespace-nowrap tw-text-[14px] tw-font-[700] tw-text-white">{nextLabel}</p>
                <NavChevronDown />
              </button>
              {nextInfo.component}
            </div>
          ) : nextInfo.isActive ? (
            <button className={`${NEXT_PILL_CLASS} ${generateNextButtonBgColor}`} onClick={onNextClick}>
              <p className="tw-whitespace-nowrap tw-text-[14px] tw-font-[700] tw-text-white">{nextLabel}</p>
              <NavChevronDown />
            </button>
          ) : (
            <button className={`${NEXT_PILL_CLASS} booking-cta-pill-disabled !tw-cursor-not-allowed`} disabled>
              <p className="tw-whitespace-nowrap tw-text-[14px] tw-font-[700]">{nextInfo.pageName}</p>
              <NavChevronDown />
            </button>
          )
        ) : null}
      </div>
    );
  },
);

NavigationButtons.displayName = "NavigationButtons";
